import {
  Card,
  Group,
  Stack,
  Switch,
  Loader,
  Center,
  Select,
  Divider,
  Text,
  SimpleGrid,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useUpdateFeaturePreferences } from 'src/Hooks/Mutations/useUpdateFeaturePreferences';
import { useUpdateUserConsents } from 'src/Hooks/Mutations/useUpdateUserConsents';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { UpdateNotificationPreferencesDto } from 'src/Services/API/generated';

import styles from './NotificationPreferences.module.scss';

enum FeatureNotificationPreference {
  All = 'ALL',
  InvolvedOnly = 'INVOLVED_ONLY',
  None = 'NONE',
}

const NotificationPreferences = () => {
  const { t } = useTranslation();
  const { data: user, isLoading: isUserLoading } = useFetchUser();
  const { mutate: updateConsents } = useUpdateUserConsents();
  const { mutate: updateFeaturePreferences } = useUpdateFeaturePreferences();

  const handleToggle = (
    key: 'transactionalEmailOptIn' | 'transactionalPushOptIn',
    value: boolean
  ) => {
    updateConsents({ [key]: value });
  };

  const handlePreferenceChange = (
    key: keyof UpdateNotificationPreferencesDto,
    value: FeatureNotificationPreference | null
  ) => {
    if (value) {
      updateFeaturePreferences({ [key]: value });
    }
  };

  const preferenceOptions = [
    {
      value: FeatureNotificationPreference.All,
      label: t('notificationPreference.options.all', 'Toutes'),
    },
    {
      value: FeatureNotificationPreference.InvolvedOnly,
      label: t('notificationPreference.options.involvedOnly', 'Seulement si je suis concerné(e)'),
    },
    {
      value: FeatureNotificationPreference.None,
      label: t('notificationPreference.options.none', 'Aucune'),
    },
  ];

  const binaryPreferenceOptions = preferenceOptions.filter(
    option => option.value !== FeatureNotificationPreference.InvolvedOnly
  );

  if (isUserLoading) {
    return (
      <Card withBorder radius="md" p="xl" style={{ minHeight: 400 }}>
        <Center style={{ height: '100%' }}>
          <Loader />
        </Center>
      </Card>
    );
  }

  return (
    <Card withBorder radius="md" p="xl">
      <fieldset style={{ border: 'none', padding: 0 }}>
        <Group justify="space-between">
          <div>
            <div className={styles.title}>
              {t('notificationPreference.page.title', 'Préférences de notification')}
            </div>
            <div className={styles.subtitle}>
              {t(
                'notificationPreference.page.subtitle',
                'Choisissez comment vous souhaitez recevoir les informations importantes.'
              )}
            </div>
          </div>
        </Group>

        <Stack mt="lg" gap="lg">
          <Group justify="space-between" wrap="nowrap">
            <div>
              <div className={styles.itemName}>
                {t('notificationPreference.page.byEmail', 'Notifications par e-mail')}
              </div>
              <div className={styles.subtitle}>
                {t(
                  'notificationPreference.page.receiveByEmail',
                  'Recevez les résumés et les alertes directement dans votre boîte mail.'
                )}
              </div>
            </div>
            <Switch
              checked={user?.transactionalEmailOptIn ?? false}
              onChange={event =>
                handleToggle('transactionalEmailOptIn', event.currentTarget.checked)
              }
            />
          </Group>
          <Group justify="space-between" wrap="nowrap">
            <div>
              <div className={styles.itemName}>
                {t('notificationPreference.page.byPush', 'Notifications push')}
              </div>
              <div className={styles.subtitle}>
                {t(
                  'notificationPreference.page.receiveByPush',
                  'Recevez des alertes en temps réel sur votre appareil.'
                )}
              </div>
            </div>
            <Switch
              checked={user?.transactionalPushOptIn ?? false}
              onChange={event =>
                handleToggle('transactionalPushOptIn', event.currentTarget.checked)
              }
            />
          </Group>
        </Stack>

        <Divider
          my="xl"
          label={
            <Text size="sm" fw={500}>
              {t('notificationPreference.page.fineGrainedTitle', 'Affiner mes notifications Push')}
            </Text>
          }
          labelPosition="center"
        />

        <Stack gap="lg">
          {/* 2. Remplacer chaque Group par une SimpleGrid responsive */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} verticalSpacing="xs">
            <div>
              <div className={styles.itemName}>
                {t('notificationPreference.page.expenses', 'Dépenses')}
              </div>
              <div className={styles.subtitle}>
                {t(
                  'notificationPreference.page.expensesSubtitle',
                  "Lorsqu'une dépense est ajoutée à l'un de vos biens."
                )}
              </div>
            </div>
            <Select
              data={preferenceOptions}
              value={user?.expenseNotificationPreference ?? null}
              onChange={value =>
                handlePreferenceChange(
                  'expenseNotificationPreference',
                  value as FeatureNotificationPreference
                )
              }
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} verticalSpacing="xs">
            <div>
              <div className={styles.itemName}>
                {t('notificationPreference.page.events', 'Événements')}
              </div>
              <div className={styles.subtitle}>
                {t(
                  'notificationPreference.page.eventsSubtitle',
                  "Lorsqu'un événement est créé dans l'un de vos biens."
                )}
              </div>
            </div>
            <Select
              data={preferenceOptions}
              value={user?.eventNotificationPreference ?? null}
              onChange={value =>
                handlePreferenceChange(
                  'eventNotificationPreference',
                  value as FeatureNotificationPreference
                )
              }
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} verticalSpacing="xs">
            <div>
              <div className={styles.itemName}>
                {t('notificationPreference.page.posts', 'Publications')}
              </div>
              <div className={styles.subtitle}>
                {t(
                  'notificationPreference.page.postsSubtitle',
                  "Lorsqu'un membre publie dans votre communauté."
                )}
              </div>
            </div>
            <Select
              data={binaryPreferenceOptions}
              value={user?.postNotificationPreference ?? null}
              onChange={value =>
                handlePreferenceChange(
                  'postNotificationPreference',
                  value as FeatureNotificationPreference
                )
              }
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} verticalSpacing="xs">
            <div>
              <div className={styles.itemName}>
                {t('notificationPreference.page.comments', 'Commentaires')}
              </div>
              <div className={styles.subtitle}>
                {t(
                  'notificationPreference.page.commentsSubtitle',
                  "Lorsqu'un post que vous suivez reçoit un nouveau commentaire."
                )}
              </div>
            </div>
            <Select
              data={binaryPreferenceOptions}
              value={user?.commentNotificationPreference ?? null}
              onChange={value =>
                handlePreferenceChange(
                  'commentNotificationPreference',
                  value as FeatureNotificationPreference
                )
              }
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} verticalSpacing="xs">
            <div>
              <div className={styles.itemName}>
                {t('notificationPreference.page.practicalInformation', 'Infos pratiques')}
              </div>
              <div className={styles.subtitle}>
                {t(
                  'notificationPreference.page.practicalInformationSubtitle',
                  "Rappels d'échéances configurés sur vos infos pratiques."
                )}
              </div>
            </div>
            <Select
              data={binaryPreferenceOptions}
              value={user?.practicalInformationNotificationPreference ?? null}
              onChange={value =>
                handlePreferenceChange(
                  'practicalInformationNotificationPreference',
                  value as FeatureNotificationPreference
                )
              }
            />
          </SimpleGrid>
        </Stack>
      </fieldset>
    </Card>
  );
};

export default NotificationPreferences;
